/**
 * ManagerRouteGuard — gates /manager/* routes.
 *
 * RoleGuard's plain hasMinRole('manager') check is satisfied by ANY role at
 * or above manager's level (5) in the numeric hierarchy — which includes
 * viewer/accountant/bookkeeper, since manager intentionally sits at the
 * bottom of ROLE_LEVEL (S25/S26 design, see useRole.ts). The backend's
 * requireManagerAccess only accepts role === 'manager' OR a member of
 * ELEVATED_ROLES (owner/admin/platform_admin) — so a bookkeeper/accountant/
 * viewer could reach these pages via the frontend gate, then get every API
 * call rejected with a confusing ROLE_MISMATCH 403 instead of being kept off
 * the page with a clear message. This guard mirrors the backend's exact
 * condition instead of RoleGuard's generic hierarchy check.
 */
import { useRole } from '@/hooks/useRole';
import { Card } from '@/components/ui/Card';

export function ManagerRouteGuard({ children }: { children: React.ReactNode }) {
  const { isManager, isAdmin } = useRole();

  if (!isManager && !isAdmin) {
    return (
      <Card padding="lg" className="text-center py-16">
        <p className="text-slate-500 dark:text-slate-400">You don't have permission to access this page.</p>
        <p className="text-sm text-slate-400 mt-2">Required role: manager (or admin/owner). Contact your organization admin for access.</p>
      </Card>
    );
  }

  return <>{children}</>;
}
